package er.prateek.crossly.generation.math

import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.data.model.math.MathCell
import er.prateek.crossly.data.model.math.MathCellKind
import er.prateek.crossly.data.model.math.MathEquation

/** One `A op B = C` equation's placement: its start cell and orientation. */
data class MathSlotSpec(val row: Int, val col: Int, val direction: ClueDirection) {
    /** The 5 (row,col) positions, in order A, op, B, =, C. */
    fun positions(): List<Pair<Int, Int>> {
        val dr = if (direction == ClueDirection.DOWN) 1 else 0
        val dc = if (direction == ClueDirection.ACROSS) 1 else 0
        return (0..4).map { row + dr * it to col + dc * it }
    }
}

private fun across(row: Int, col: Int) = MathSlotSpec(row, col, ClueDirection.ACROSS)
private fun down(row: Int, col: Int) = MathSlotSpec(row, col, ClueDirection.DOWN)

/**
 * A blank math-crossword shape: a set of equation slots laid on a grid. Templates are kept
 * **acyclic** (the graph of equations-that-share-a-number-cell is a tree/forest) so the generator
 * can always fill them, with any operators, in one pass -- see [MathCrosswordGenerator]. Shapes are
 * intentionally irregular (a spine with equations hanging through it), never a fully-coupled square.
 */
data class MathTemplate(
    val id: String,
    val slots: List<MathSlotSpec>,
) {
    val equations: Int = slots.size
    val width: Int = slots.flatMap { it.positions() }.maxOf { it.second } + 1
    val height: Int = slots.flatMap { it.positions() }.maxOf { it.first } + 1

    /** Builds the row-major cell-kind grid. Only NUMBER cells may be shared between slots. */
    fun kindGrid(): List<MathCellKind> {
        val grid = MutableList(width * height) { MathCellKind.BLOCK }
        for (slot in slots) {
            slot.positions().forEachIndexed { offset, (r, c) ->
                val kind = when (offset) {
                    1 -> MathCellKind.OPERATOR
                    3 -> MathCellKind.EQUALS
                    else -> MathCellKind.NUMBER
                }
                val idx = r * width + c
                val existing = grid[idx]
                grid[idx] = when {
                    existing == MathCellKind.BLOCK -> kind
                    existing == kind && kind == MathCellKind.NUMBER -> kind
                    else -> error("Template ${id} has conflicting cells at ($r,$c)")
                }
            }
        }
        return grid
    }

    /** The equations as flat-index tuples `[A, op, B, =, C]`. */
    fun equationList(): List<MathEquation> = slots.map { slot ->
        val idx = slot.positions().map { (r, c) -> r * width + c }
        MathEquation(cells = idx, direction = slot.direction)
    }

    /** A fresh all-BLOCK set of [MathCell]s to be filled in by the generator. */
    fun blankCells(): MutableList<MathCell> =
        kindGrid().mapTo(mutableListOf()) { MathCell(kind = it) }
}

/**
 * The shape pool, grouped by equation count. A tier moves up through the counts as its level index
 * climbs (see [MathCrosswordGenerator]), and [select] rotates through the shapes of a given count by
 * index so consecutive same-complexity levels still look different -- the grid keeps changing.
 */
object MathGridTemplates {
    // --- 2 equations: a single crossing, in several arrangements.
    private val cross = MathTemplate("cross", listOf(across(2, 0), down(0, 2)))            // share (2,2)
    private val cornerTopLeft = MathTemplate("corner-tl", listOf(across(0, 0), down(0, 0))) // share (0,0)
    private val cornerBottomRight = MathTemplate("corner-br", listOf(across(4, 0), down(0, 4))) // share (4,4)
    private val teeTop = MathTemplate("tee-top", listOf(across(0, 0), down(0, 2)))          // share (0,2)
    private val teeBottom = MathTemplate("tee-bottom", listOf(across(4, 0), down(0, 2)))    // share (4,2)
    private val teeLeft = MathTemplate("tee-left", listOf(across(2, 0), down(0, 0)))        // share (2,0)
    private val teeRight = MathTemplate("tee-right", listOf(across(2, 0), down(0, 4)))      // share (2,4)

    // --- 3 equations.
    private val doubleDown = MathTemplate("double-down", listOf(across(2, 0), down(0, 0), down(0, 4)))      // across + downs at both ends
    private val doubleDownMid = MathTemplate("double-down-mid", listOf(across(2, 0), down(0, 0), down(0, 2))) // across + left + middle downs
    private val doubleAcross = MathTemplate("double-across", listOf(down(0, 2), across(0, 0), across(4, 0)))  // down spine + two acrosses

    // --- 4 equations.
    private val comb = MathTemplate("comb", listOf(across(2, 0), down(0, 0), down(0, 2), down(0, 4)))        // horizontal spine, 3 teeth
    private val ladder = MathTemplate("ladder", listOf(down(0, 2), across(0, 0), across(2, 0), across(4, 0))) // vertical spine, 3 rungs

    private val byEquations: Map<Int, List<MathTemplate>> = mapOf(
        2 to listOf(cross, teeTop, teeLeft, cornerTopLeft, teeRight, teeBottom, cornerBottomRight),
        3 to listOf(doubleDown, doubleAcross, doubleDownMid),
        4 to listOf(comb, ladder),
    )

    /** Picks a shape with [equations] equations, rotating by [rotation] (the level index) for variety. */
    fun select(equations: Int, rotation: Int): MathTemplate {
        val group = byEquations[equations] ?: byEquations.getValue(2)
        val i = ((rotation - 1) % group.size + group.size) % group.size
        return group[i]
    }

    /** Every shape, for validation/tests. */
    val all: List<MathTemplate> = byEquations.values.flatten()
}
